import { useState } from 'react';
import {
  Box, Title, Text, Button, Group, Table, ActionIcon, Badge, Modal,
  TextInput, Select, Textarea, Stack, Paper, Menu, Tooltip,
} from '@mantine/core';
import { IconPlus, IconPencil, IconTrash, IconVariable, IconDotsVertical } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { variablesApi, Variable } from '@/services/variables.api';

const TYPE_COLORS: Record<string, string> = {
  STRING: 'blue',
  NUMBER: 'green',
  BOOLEAN: 'orange',
};

export default function VariablesPage() {
  const queryClient = useQueryClient();
  const [modalOpened, setModalOpened] = useState(false);
  const [editingVar, setEditingVar] = useState<Variable | null>(null);
  const [form, setForm] = useState({ name: '', type: 'STRING' as 'STRING' | 'NUMBER' | 'BOOLEAN', defaultValue: '', description: '' });

  const { data: variables = [], isLoading } = useQuery({
    queryKey: ['variables', 'global'],
    queryFn: variablesApi.getGlobalVariables,
  });

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof variablesApi.createVariable>[0]) => variablesApi.createVariable(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variables'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof variablesApi.updateVariable>[1] }) =>
      variablesApi.updateVariable(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variables'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: variablesApi.deleteVariable,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['variables'] }),
  });

  const openCreate = () => {
    setEditingVar(null);
    setForm({ name: '', type: 'STRING', defaultValue: '', description: '' });
    setModalOpened(true);
  };

  const openEdit = (v: Variable) => {
    setEditingVar(v);
    setForm({ name: v.name, type: v.type, defaultValue: v.defaultValue || '', description: v.description || '' });
    setModalOpened(true);
  };

  const closeModal = () => {
    setModalOpened(false);
    setEditingVar(null);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;

    if (editingVar) {
      updateMutation.mutate({ id: editingVar.id, data: form });
    } else {
      createMutation.mutate({ ...form, scope: 'GLOBAL' });
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2} c="#2A3140">Global Variables</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Variables available across all flows. Use <code>*variableName*</code> to insert in page content.
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} style={{ backgroundColor: '#415A91' }} radius="md" onClick={openCreate}>
          Add Variable
        </Button>
      </Group>

      {variables.length === 0 && !isLoading ? (
        <Paper p="xl" radius="md" withBorder ta="center">
          <IconVariable size={48} style={{ color: '#ADB5BD' }} />
          <Text size="lg" fw={500} mt="md" c="#2A3140">No global variables yet</Text>
          <Text size="sm" c="dimmed" mt={4}>
            Create variables that can be used across all your flows
          </Text>
          <Button mt="lg" variant="light" color="blue" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Create your first variable
          </Button>
        </Paper>
      ) : (
        <Paper radius="md" withBorder style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Default Value</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th w={80}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {variables.map((v) => (
                <Table.Tr key={v.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{v.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={TYPE_COLORS[v.type]} variant="light">{v.type}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{v.defaultValue || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={1}>{v.description || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" shadow="md">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => openEdit(v)}>
                          Edit
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => deleteMutation.mutate(v.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingVar ? 'Edit Variable' : 'Create Global Variable'}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Variable Name"
            placeholder="e.g. companyName"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            description="Use camelCase. This will be referenced as *variableName* in page content."
          />
          <Select
            label="Type"
            data={[
              { value: 'STRING', label: 'String' },
              { value: 'NUMBER', label: 'Number' },
              { value: 'BOOLEAN', label: 'Boolean' },
            ]}
            value={form.type}
            onChange={(v) => setForm({ ...form, type: (v as any) || 'STRING' })}
          />
          <TextInput
            label="Default Value"
            placeholder={form.type === 'BOOLEAN' ? 'true / false' : 'Default value'}
            value={form.defaultValue}
            onChange={(e) => setForm({ ...form, defaultValue: e.target.value })}
          />
          <Textarea
            label="Description"
            placeholder="What is this variable used for?"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeModal}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              style={{ backgroundColor: '#415A91' }}
            >
              {editingVar ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
